import React, { useCallback } from 'react';

// Globals
import gql from 'graphql-tag';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';

// UI
import { PlusCircleOutlined } from '@ant-design/icons';

import { Button, message, Modal, Tooltip } from 'antd';
import { Droppable, DragDropContext } from 'react-beautiful-dnd';

// Locals
import {
  deleteStep,
  setNewStepModalVisible,
  setOpenedStep,
  setCardProps,
  reorderSteps,
  setEditingChapterId,
  setGiideTabState,
} from '../actions';
import {
  DeleteStepInput,
  DeleteStepMutation,
  StepReorderInput,
  StepReorderMutation,
} from '../graphql/api';
import useMutation from '../hooks/useMutation';
import useGiide from '../hooks/useQuery/useGiide';
import styles from './Components.module.css';
import { sortStepsByOrder } from './utils';

// Types
import type { GiideRouteParams } from '../types';
import type { Step } from '../graphql/types';
import { IState } from '../graphql/types';
import useUser from '../hooks/useUser';
import StepMenuItem from './StepMenuItem';
import { colors } from '../theme';

const deleteStepMutation = gql`
  mutation DeleteStep($input: DeleteStepInput!) {
    deleteStep(input: $input) {
      id
    }
  }
`;

const stepReorderMutation = gql`
  mutation StepReorder($input: StepReorderInput!) {
    stepReorder(input: $input) {
      id
      success
      error
    }
  }
`;

const GiideSideMenuLeft: React.FC = () => {
  const dispatch = useDispatch();
  const { push } = useHistory();
  const { pathname } = useLocation();
  const { id } = useParams<GiideRouteParams>();
  if (!id) {
    push('/');
  }

  const { user } = useUser();

  const { steps, openedStep, data } = useGiide(id);
  const editingChapterId = useSelector<IState, string | undefined>(
    state => state.editingChapterId ?? undefined
  );

  const deleteMutation = useMutation<DeleteStepInput, DeleteStepMutation>(
    deleteStepMutation
  );
  const reorderMutation = useMutation<StepReorderInput, StepReorderMutation>(
    stepReorderMutation
  );

  const rollbackReorderSteps = React.useCallback(
    (
      stepId: string,
      stepNewIndex: number,
      errorMessage: string | null | undefined
    ) => {
      message.error(errorMessage ?? 'Failed to reorder steps');
      dispatch(reorderSteps(stepId, stepNewIndex));
    },
    [dispatch]
  );

  const onDragEnd = React.useCallback(
    async dnd => {
      if (!dnd.destination) {
        return;
      }
      const stepId = steps[dnd.source.index].id;
      dispatch(reorderSteps(steps[dnd.source.index].id, dnd.destination.index));
      const reorder = await reorderMutation
        .callMutation({
          stepId,
          newStepOrder: dnd.destination.index,
          giideId: data!.id,
        })
        .catch(err => {
          rollbackReorderSteps(
            steps[dnd.destination.index].id,
            dnd.source.index,
            'Failed to reorder steps'
          );
        });
      if (reorder && !reorder.data.stepReorder?.success) {
        rollbackReorderSteps(
          steps[dnd.destination.index].id,
          dnd.source.index,
          reorder.data.stepReorder?.error
        );
      }
    },
    [steps, dispatch, reorderMutation, rollbackReorderSteps, data]
  );

  const [modal, contextHolder] = Modal.useModal();

  const handleStepChange = useCallback(
    step => {
      batch(() => {
        dispatch(setGiideTabState(undefined));
        dispatch(setOpenedStep(step.order));
      });

      if (pathname !== `/giide/${id}`) push(`/giide/${id}`);
    },
    [dispatch, id, pathname, push]
  );

  const onDeleteStep = React.useCallback(
    step => {
      modal.confirm({
        centered: true,
        closable: true,
        cancelText: 'Cancel',
        okText: 'Delete',
        bodyStyle: { margin: '16px 0' },
        okType: 'danger',
        onOk: async () => {
          try {
            const stepDeleted = await deleteMutation.callMutation({
              id: step.id,
            });
            if (stepDeleted.data.deleteStep) dispatch(deleteStep(step.id));
          } catch (err) {
            message.error('An error during deleting');
          }
          return true;
        },
        title: 'Delete: ' + step.title,
        content:
          'Are you sure you want to delete this chapter? The audio and feed cards in this chapter will be permanently deleted.',
      });
    },
    [deleteMutation, dispatch, modal]
  );

  const maxChapters = React.useMemo(() => {
    let max = 4;
    if (user?.plan?.features?.chapters?.maxNumberOfChapters) {
      max = user?.plan?.features?.chapters?.maxNumberOfChapters;
    }

    return max;
  }, [user?.plan?.features?.chapters?.maxNumberOfChapters]);

  return (
    <div>
      {contextHolder}
      <div className={styles.giideMenuTitle}>
        <span>Chapters</span>
        {!data?.giideLibraryId &&
          (steps.length < maxChapters ? (
            <Button
              type={'text'}
              onClick={() => {
                batch(() => {
                  dispatch(setCardProps());
                  dispatch(setNewStepModalVisible(true));
                  dispatch(setEditingChapterId(undefined));
                });
              }}
              icon={<PlusCircleOutlined style={{ color: colors.black3 }} />}
            ></Button>
          ) : (
            <Tooltip
              placement="right"
              title={`You've reached the maximum amount of chapters (${maxChapters}).`}
            >
              <Button
                type={'text'}
                disabled={true}
                icon={<PlusCircleOutlined />}
              ></Button>
            </Tooltip>
          ))}
      </div>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided, snapshot) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {(steps as Step[])
                .sort(sortStepsByOrder)
                .map((step, stepIndex) => (
                  <StepMenuItem
                    key={step.id}
                    step={step}
                    data={data}
                    onDeleteStep={onDeleteStep}
                    editingChapterId={editingChapterId}
                    stepIndex={stepIndex}
                    openedStep={openedStep}
                    handleStepChange={handleStepChange}
                  />
                ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default GiideSideMenuLeft;
